import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { trackEdgeFunctionRequest } from '@/lib/monitoring';

// Use variáveis de ambiente do Vite (prefixed with VITE_)
// Para desenvolvimento local, use: http://localhost:54321
// Para produção, use a URL do seu projeto Supabase
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/** Serialized refresh so many parallel 401s share one refreshSession() instead of a thundering herd. */
let refreshAccessTokenInFlight: Promise<string | null> | null = null;

function getRequestUrlString(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function requestHasBearerAuth(input: RequestInfo | URL, init?: RequestInit): boolean {
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  const auth = headers.get("authorization") ?? headers.get("Authorization");
  return !!auth?.toLowerCase().startsWith("bearer ");
}

/**
 * PostgREST JWT problems (expired, malformed header, bad_jwt code, etc.).
 * refreshSession() can replace a bad access token if the refresh token in storage is still valid.
 */
function isRecoverableJwtRestError(status: number, body: unknown): boolean {
  if (status !== 401 && status !== 403) return false;
  if (!body || typeof body !== "object") return false;
  const o = body as Record<string, unknown>;
  const code = typeof o.code === "string" ? o.code : "";
  const message = typeof o.message === "string" ? o.message : "";
  if (code === "PGRST301" || code === "PGRST302" || code === "PGRST303") return true;
  if (code === "bad_jwt") return true;
  return /jwt expired|invalid jwt|token is expired|token is malformed|invalid number of segments|unable to parse or verify/i.test(
    message,
  );
}

function getRefreshedAccessToken(client: SupabaseClient<Database>): Promise<string | null> {
  if (!refreshAccessTokenInFlight) {
    refreshAccessTokenInFlight = client.auth
      .refreshSession()
      .then(({ data, error }) => {
        if (error || !data.session?.access_token) return null;
        return data.session.access_token;
      })
      .catch(() => null)
      .finally(() => {
        refreshAccessTokenInFlight = null;
      });
  }
  return refreshAccessTokenInFlight;
}

/**
 * When the access token expires, many concurrent fetches can all fail before auto-refresh completes.
 * This wraps fetch: on JWT-expired responses, runs a single shared refresh and retries once with the new bearer.
 */
function createSupabaseRuntimeFetch(clientHolder: { client: SupabaseClient<Database> | null }): typeof fetch {
  return async (input, init) => {
    const url = getRequestUrlString(input);
    trackEdgeFunctionRequest(url, init);

    const response = await globalThis.fetch(input, init);
    const client = clientHolder.client;

    if (
      !client ||
      !SUPABASE_URL ||
      !url.startsWith(SUPABASE_URL) ||
      !requestHasBearerAuth(input, init)
    ) {
      return response;
    }

    if (response.status !== 401 && response.status !== 403) {
      return response;
    }

    let body: unknown = null;
    try {
      body = await response.clone().json();
    } catch {
      return response;
    }

    if (!isRecoverableJwtRestError(response.status, body)) {
      return response;
    }

    const accessToken = await getRefreshedAccessToken(client);
    if (!accessToken) {
      return response;
    }

    if (input instanceof Request) {
      const headers = new Headers(input.headers);
      headers.set("Authorization", `Bearer ${accessToken}`);
      return globalThis.fetch(new Request(input, { headers }));
    }

    const headers = new Headers(init?.headers);
    headers.set("Authorization", `Bearer ${accessToken}`);
    return globalThis.fetch(input, { ...init, headers });
  };
}

const supabaseClientHolder: { client: SupabaseClient<Database> | null } = { client: null };
const runtimeFetch = createSupabaseRuntimeFetch(supabaseClientHolder);

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    fetch: runtimeFetch,
  },
});

supabaseClientHolder.client = supabase;
